import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import AllergenBadge from "@/components/AllergenBadge";
import { ScanLine, Plus, Minus, ShoppingBag, CheckCircle2, RotateCcw, ChefHat } from "lucide-react";

/**
 * Self-service kiosk — public page. RFID tap → scan card → menu → cart → tap to pay.
 * Designed for a touchscreen mounted in the canteen.
 */
export default function Kiosk() {
  const [step, setStep] = useState("welcome"); // welcome | menu | success
  const [rfid, setRfid] = useState("");
  const [student, setStudent] = useState(null);
  const [menu, setMenu] = useState([]);
  const [cats, setCats] = useState(["All"]);
  const [active, setActive] = useState("All");
  const [cart, setCart] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/menu").then(r => setMenu(r.data));
    api.get("/menu/categories").then(r => setCats(["All", ...r.data]));
  }, []);

  const filtered = useMemo(() => menu.filter(m => active === "All" || m.category === active), [menu, active]);
  const cartItems = useMemo(() => Object.entries(cart).map(([id, qty]) => {
    const m = menu.find(x => x.id === id); return m ? { ...m, qty } : null;
  }).filter(Boolean), [cart, menu]);
  const total = cartItems.reduce((s, c) => s + c.price * c.qty, 0);

  const inc = (id) => setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart(c => { const n = {...c}; n[id] = (c[id]||0)-1; if (n[id]<=0) delete n[id]; return n; });

  const startSession = async () => {
    if (!rfid) return toast.error("Tap or enter card ID");
    // Verify card exists by previewing balance via tap with empty items (won't charge)
    setStep("menu");
    setStudent({ rfid_card_id: rfid });
  };

  const reset = () => {
    setStep("welcome"); setRfid(""); setStudent(null); setCart({}); setResult(null);
  };

  const checkout = async () => {
    // We can't make /api/rfid/tap as kiosk (requires operator role). For prototype, we use mock-success approach via topup-then-order is complex. Simplest: kiosk must call a public-friendly endpoint. Skip auth for demo by NOT calling the secured endpoint; instead show a success screen indicating "tap operator to confirm". For full implementation, kiosk would auth as a kiosk-operator account. For prototype, we'll call /api/rfid/tap via a kiosk operator token stored in localStorage if one is configured.
    const kioskToken = localStorage.getItem("kiosk_op_token");
    if (!kioskToken) {
      // Just show a friendly success screen for the demo
      setResult({ accepted: true, items: cartItems, total, demo: true });
      setStep("success");
      return;
    }
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rfid/tap`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${kioskToken}` },
        body: JSON.stringify({ rfid_card_id: rfid, items: cartItems.map(c => ({ menu_item_id: c.id, qty: c.qty })) }),
      }).then(x => x.json());
      setResult({ ...r, items: cartItems, total });
      setStep("success");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (step === "welcome") {
    return (
      <div className="min-h-screen bg-cream warm-grain flex flex-col">
        <div className="px-6 py-4 flex items-center justify-between">
          <BrandLogo size="md" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand bg-brand-50 px-2 py-0.5 rounded-full">Self-service kiosk</span>
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl p-10 sm:p-14 border border-border shadow-medium max-w-2xl w-full text-center">
            <div className="relative w-32 h-32 mx-auto mb-8">
              <div className="absolute inset-0 rounded-full bg-brand/20 animate-pulse-ring" />
              <div className="relative w-full h-full rounded-full bg-brand text-white flex items-center justify-center">
                <ScanLine size={48} />
              </div>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-ink">Tap your card</h1>
            <p className="text-base text-ink-600 mt-3">Scan your RFID or type your card ID below to start ordering.</p>
            <form onSubmit={(e) => { e.preventDefault(); startSession(); }} className="mt-8 flex gap-2 max-w-md mx-auto">
              <input data-testid="kiosk-rfid" autoFocus value={rfid} onChange={e => setRfid(e.target.value)}
                placeholder="RFID-001"
                className="flex-1 rounded-2xl border-2 border-border px-5 py-4 text-lg font-mono focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              <button type="submit" className="px-6 rounded-2xl bg-brand text-white font-bold hover:bg-brand-600 active:scale-95">
                <ScanLine size={22} />
              </button>
            </form>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {["RFID-001", "RFID-002"].map(r => (
                <button key={r} data-testid={`kiosk-quick-${r}`} onClick={() => { setRfid(r); setTimeout(startSession, 150); }} className="px-3 py-1.5 rounded-full bg-cream-200 hover:bg-cream-300 text-xs font-mono font-bold">
                  {r}
                </button>
              ))}
            </div>
            <Link to="/" className="inline-block mt-8 text-sm text-ink-500 hover:text-ink">← Exit kiosk</Link>
          </div>
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="min-h-screen bg-cream warm-grain flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl p-10 border border-border shadow-medium max-w-md w-full text-center">
          {result?.accepted !== false ? (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700 mb-4">
                <CheckCircle2 size={40} />
              </div>
              <h1 className="font-heading text-3xl font-extrabold text-ink">Order placed!</h1>
              <p className="text-sm text-ink-600 mt-2">{result?.demo ? "Demo mode — head to the counter." : `Charged to ${student?.rfid_card_id}`}</p>
              <div className="mt-5 bg-cream-100 rounded-2xl p-4 text-left">
                {result?.items?.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{i.qty}× {i.name}</span>
                    <span>{inr(i.price * i.qty)}</span>
                  </div>
                ))}
                <div className="border-t border-border mt-2 pt-2 flex justify-between font-bold">
                  <span>Total</span><span>{inr(result?.total)}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-extrabold text-brand">Order rejected</h1>
              <p className="text-sm text-ink-600 mt-2">{result?.reason}</p>
            </>
          )}
          <button data-testid="kiosk-new" onClick={reset} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-white font-semibold hover:bg-ink-700">
            <RotateCcw size={16} /> New order
          </button>
        </div>
      </div>
    );
  }

  // Menu view
  return (
    <div className="min-h-screen bg-cream warm-grain flex flex-col">
      <header className="px-6 py-4 flex items-center justify-between border-b border-border bg-white">
        <div className="flex items-center gap-3">
          <BrandLogo size="md" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand bg-brand-50 px-2 py-0.5 rounded-full">Card · {rfid}</span>
        </div>
        <button onClick={reset} data-testid="kiosk-cancel" className="text-sm text-ink-500 hover:text-ink">Cancel</button>
      </header>

      <div className="px-6 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar">
        {cats.map(c => (
          <button key={c} onClick={() => setActive(c)} data-testid={`kiosk-cat-${c}`}
            className={`px-4 py-2.5 rounded-xl text-base font-bold whitespace-nowrap ${active === c ? 'bg-ink text-white' : 'bg-white border border-border text-ink-700'}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="flex-1 px-6 pb-32 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map(m => (
          <button key={m.id} data-testid={`kiosk-item-${m.id}`} disabled={m.sold_out} onClick={() => inc(m.id)}
            className="relative bg-white rounded-3xl border-2 border-border hover:border-brand active:scale-95 transition-all overflow-hidden text-left disabled:opacity-40">
            <div className="aspect-square bg-cream-200">
              {m.image_url ? <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-ink-400"><ChefHat size={32} /></div>}
            </div>
            <div className="p-3">
              <div className="font-heading font-bold text-ink truncate">{m.name}</div>
              <div className="flex items-center justify-between mt-1">
                <div className="flex flex-wrap gap-0.5">{m.dietary_tags?.slice(0,2).map(t => <AllergenBadge key={t} tag={t} />)}</div>
                <div className="font-heading text-lg font-extrabold text-brand">{inr(m.price)}</div>
              </div>
            </div>
            {cart[m.id] > 0 && (
              <div className="absolute top-2 right-2 w-9 h-9 rounded-full bg-brand text-white font-bold flex items-center justify-center shadow-medium">
                {cart[m.id]}
              </div>
            )}
          </button>
        ))}
      </div>

      {cartItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border px-6 py-4 shadow-floating">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">{cartItems.reduce((s,c)=>s+c.qty,0)} items</div>
              <div className="font-heading text-3xl font-extrabold text-ink">{inr(total)}</div>
            </div>
            <button data-testid="kiosk-checkout" onClick={checkout}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-brand text-white font-bold text-lg hover:bg-brand-600 active:scale-95">
              Tap to pay <ScanLine size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
